/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The pure rules behind the permissions editor: what the company cap disables,
 * how a stored list becomes editor state, and what a save would switch off.
 */

import { REGISTERED_SLUGS, insightName } from '@/insights/insightCatalogue'

import { APP_GRANT_LABELS, type AppGrant, type PermissionsCap, type PermissionsValue } from './types'
import type { UsageIndex } from './usage'

/** A saved company profile caps apps by its grant list (an empty list means none); no profile caps nothing. */
export const isGrantCapped = (cap: PermissionsCap | null, grant: AppGrant): boolean =>
  !!cap && cap.hasProfile && !cap.grants.includes(grant)

/** Same for insights: a saved profile allows exactly what it lists. */
export const isInsightCapped = (cap: PermissionsCap | null, slug: string): boolean =>
  !!cap && cap.hasProfile && !cap.insights.includes(slug)

export const cappedTooltip = (label: string, companyName: string): string =>
  `${label} is not enabled for ${companyName}`

/** Editor state for a stored insights list: exactly what is stored, minus slugs no longer registered. */
export const registeredInsights = (stored: readonly string[]): string[] =>
  stored.filter((slug) => REGISTERED_SLUGS.includes(slug))

/** Drop whatever the cap disables, reporting the display names that went. */
export const applyCap = (
  value: PermissionsValue,
  cap: PermissionsCap | null
): { value: PermissionsValue; removed: string[] } => {
  const removed: string[] = []
  const grants = value.grants.filter((grant) => {
    if (!isGrantCapped(cap, grant)) return true
    removed.push(APP_GRANT_LABELS[grant])
    return false
  })
  const insights = value.insights.filter((slug) => {
    if (!isInsightCapped(cap, slug)) return true
    removed.push(insightName(slug))
    return false
  })
  return removed.length === 0 ? { value, removed } : { value: { grants, insights }, removed }
}

/** What `after` switches off that `before` had and that is currently in use. */
export const disabledInUse = (
  before: PermissionsValue,
  after: PermissionsValue,
  usage: UsageIndex
): { grants: AppGrant[]; insights: string[] } => ({
  grants: before.grants.filter((grant) => !after.grants.includes(grant) && usage.inUse(grant)),
  insights: before.insights.filter((slug) => !after.insights.includes(slug) && usage.inUse(slug)),
})

/** Order-insensitive form of a value, for dirty comparison. */
export const normaliseValue = (value: PermissionsValue): PermissionsValue => ({
  grants: [...value.grants].sort(),
  insights: [...value.insights].sort(),
})
